// The issue page's 3D canvas.
//
// Until now the issue page had a 3D *thumbnail*: a viewport with six
// buttons, collapsed by default, with no way to find an element, hide a
// trade, or look at anything the camera did not happen to be pointing
// at. Anyone who needed to actually work with the model left for
// /personal-area/projects/model-view and lost the issue's context.
//
// This is the model-view toolset brought to the issue: a browsable tree
// (category → type → element, from GET /commits/model-tree), per-node
// and per-element show/hide, isolate, selection with properties, the
// camera and inspection tools, and the resolution's diff painted on top
// when there is one.
//
// It mounts LAZILY — the parent renders it only when the 3D surface is
// the one being shown — because a WebGL context plus a streaming session
// is not something to spin up for an issue whose evidence is a photo.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Input, Tooltip, Empty, Spin, message } from "antd";
import {
  EyeOutlined, EyeInvisibleOutlined, ColumnWidthOutlined, PlusOutlined,
  SearchOutlined, MenuFoldOutlined, MenuUnfoldOutlined, DownOutlined, RightOutlined,
  HistoryOutlined, PushpinOutlined, PaperClipOutlined, CloseOutlined,
  SelectOutlined,
} from "@ant-design/icons";
import { Vector3 } from "three";
import useViewpointCamera, { viewpointIsolateTitle } from "hooks/useViewpointCamera";
import WebGlApp from "webgl";
import { url } from "api";
import { getModelTree, getCommitDiff } from "api/commits/commitsApi";
import { getIssueModelSources, setIssueSourceModel, setIssueMarkupView } from "api/issue/issueApi";
import { applyDiffOverlay, diffLegendRows } from "helpers/diffOverlay";
import moment from "moment";
import { T } from "pages/Issue/theme";
import Issue3DPins from "pages/Issue/components/Issue3DPins";
import ViewerViewButtons from "components/Viewer/ViewerViewButtons";
import ViewerToolbar from "components/Viewer/ViewerToolbar";
import { attachBuildingGrid } from "helpers/buildingGridLoad";

// The viewpoint's two pieces of UI, as components rather than as conditionals
// inside IssueModelCanvas. That component sits exactly on
// eslint-plugin-react-hooks' code-path limit: two more inline branches in its
// JSX tipped it over and it reported "hook called conditionally" at a hook two
// hundred lines earlier, which fails the build. Deciding to render NOTHING in
// here costs the big component no branch at all.
function ViewpointIsolateButton({ viewpoint, Btn }) {
  if (!viewpoint.uids.length) return null;
  return (
    <Btn
      title={viewpointIsolateTitle(viewpoint.uids.length, viewpoint.isolated)}
      active={viewpoint.isolated}
      icon={<SelectOutlined />}
      onClick={viewpoint.toggleIsolation}
    />
  );
}

function ViewpointNote({ note }) {
  if (!note) return null;
  return <span style={{ opacity: 0.7 }}>· {note}</span>;
}

// The page-level "candidate fix elements" effect needs the live WebGlApp
// of whichever canvas is mounted; the canvas registers itself here.
export const activeIssueViewer = { current: null };

const RAIL_WIDTH = 268;

// The rail's own git-shaped counts. Same order and same reading as
// GeomDelta on the light surfaces (+added ~modified −deleted), tuned for
// the dark rail: a zero stays visible but recedes, so the shape of the
// row never changes and a node that this push did not touch says so
// rather than going blank.
const NODE_CHANGE_COLORS = { added: "#4FC92A", modified: "#E0A43A", removed: "#FB8080" };

function NodeDelta({ change, dim = false }) {
  const c = change || {};
  const a = c.added || 0;
  const m = c.modified || 0;
  const r = c.removed || 0;
  const cell = (prefix, n, color) => (
    <span style={{
      color: n ? color : "rgba(215,221,226,.28)",
      fontWeight: n ? 700 : 500,
      fontVariantNumeric: "tabular-nums",
    }}>{prefix}{n}</span>
  );
  return (
    <Tooltip title={`${a} added · ${m} changed · ${r} deleted by this revision`}>
      <span style={{
        display: "inline-flex", gap: 4, fontSize: 9.5, lineHeight: 1,
        fontFamily: "SFMono-Regular, Menlo, Consolas, monospace",
        opacity: dim ? 0.5 : 1, flexShrink: 0,
      }}>
        {cell("+", a, NODE_CHANGE_COLORS.added)}
        {cell("~", m, NODE_CHANGE_COLORS.modified)}
        {cell("\u2212", r, NODE_CHANGE_COLORS.removed)}
      </span>
    </Tooltip>
  );
}

// One element's verdict, as a single leading glyph so a long list of
// elements still scans down its left edge.
const ELEMENT_MARK = {
  added: { glyph: "+", color: NODE_CHANGE_COLORS.added, label: "added by this revision" },
  modified: { glyph: "~", color: NODE_CHANGE_COLORS.modified, label: "changed by this revision" },
  removed: { glyph: "\u2212", color: NODE_CHANGE_COLORS.removed, label: "deleted by this revision" },
};

function ElementMark({ change }) {
  const m = ELEMENT_MARK[change];
  if (!m) return <span style={{ width: 9, flexShrink: 0, display: "inline-block" }} />;
  return (
    <Tooltip title={m.label}>
      <span style={{
        width: 9, flexShrink: 0, display: "inline-block", color: m.color,
        fontWeight: 700, fontSize: 11, fontFamily: "SFMono-Regular, Menlo, Consolas, monospace",
      }}>{m.glyph}</span>
    </Tooltip>
  );
}

const nodeTouched = (ch) => !!(ch && ((ch.added || 0) + (ch.modified || 0) + (ch.removed || 0)));

// Which revision the canvas is describing, in order of immediacy: the
// version the reader is previewing, the resolution the page handed down,
// then the push the issue itself was raised against.
//
// A plain function rather than an inline `||` chain: this project's
// eslint-plugin-react-hooks mis-reads a four-way short-circuit as a
// branch and calls every hook below it conditional, which fails the
// build. Out here it is also easier to read.
function revisionOnScreen({ previewing, preview, review, issue }) {
  if (previewing) return preview && preview.commitId ? String(preview.commitId) : "";
  if (review && review.active && review.commitId) return String(review.commitId);
  const own = issue && issue.sourceModel && issue.sourceModel.diffCommitId;
  return own ? String(own) : "";
}

function ToolBtn({ icon, onClick, title, active, id }) {
  return (
    <Tooltip title={title}>
      <button
        type="button"
        id={id}
        onClick={onClick}
        style={{
          width: 28, height: 28, borderRadius: 4, border: "none",
          background: active ? "rgba(254,105,25,.9)" : "rgba(255,255,255,.08)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#fff", cursor: "pointer",
        }}
      >{icon}</button>
    </Tooltip>
  );
}

export default function IssueModelCanvas({
  issue, canEdit, userInfo, onIssueUpdated, review = null,
  fill = true, height,
}) {
  const viewerRef = useRef(null);
  const WebGL = useRef(null);
  const pinModeRef = useRef(false);

  const [isGLLoaded, setIsGLLoaded] = useState(false);
  const [pct, setPct] = useState(0);
  const [errText, setErrText] = useState(null);
  const [pinMode, setPinMode] = useState(false);
  const [pendingPinUniqueId, setPendingPinUniqueId] = useState(null);
  const [railOpen, setRailOpen] = useState(true);

  // Tree state
  const [tree, setTree] = useState(null);        // null = loading, [] = none
  const [treeSource, setTreeSource] = useState(null);
  // What the revision on screen did, per node - see the review tree in
  // back/routes/commits.js.
  const [treeChange, setTreeChange] = useState(null);
  // Show only the rows this push touched. Off by default: the tree is
  // still the model's index first and the revision's second.
  const [changedOnly, setChangedOnly] = useState(false);
  const [treeErr, setTreeErr] = useState("");
  const [expanded, setExpanded] = useState({});  // categoryKey -> true
  const [elementsBy, setElementsBy] = useState({}); // "cat" | "cat|type" -> [element]
  const [loadingNode, setLoadingNode] = useState("");
  const [hiddenNodes, setHiddenNodes] = useState({}); // node key -> true
  const [q, setQ] = useState("");

  // Selection (uniqueIds), mirrored from the viewer so tree clicks and
  // canvas clicks agree on what is selected.
  const [selection, setSelection] = useState([]);
  // The canvas click handler lives inside a ReactApi built once at mount, so
  // it cannot close over selectUid directly without freezing the first
  // render's selection. The ref is written on every render below.
  const selectUidRef = useRef(null);
  const [selectedMeta, setSelectedMeta] = useState(null);

  useEffect(() => { pinModeRef.current = pinMode; }, [pinMode]);

  // A version the reader picked in the VERSIONS panel, streamed instead
  // of the issue's own source without touching the issue. Attaching is a
  // separate, explicit act.
  const [preview, setPreview] = useState(null);   // { manifestPath, label, commitId, kind }
  const [sources, setSources] = useState(null);   // { commits, timelines, issues }
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [attaching, setAttaching] = useState("");

  const projectId = issue?.projectId?._id || issue?.projectId || "";
  const zipInfo = issue?.sourceModel?.zipInfo || issue?.sourceModelZipInfo;
  const isIfcProject = !!(issue?.sourceModel?.isIfcProject);
  // Issues pinned to a push before 2026-09-06 hold the bare R2 key
  // (`<projectId>/versions/<exportVersion>/manifest.json.gz`); the static
  // route serves it under uploads/, and fetching the bare key returns the
  // SPA's index.html. Newer pins arrive prefixed; older ones are fixed here.
  const withUploadsPrefix = (p) =>
    (/^[0-9a-f]{24}\/versions\//i.test(String(p || "")) ? `uploads/${p}` : (p || ""));
  const manifestPath = withUploadsPrefix(
    issue?.sourceModel?.manifestPath
    || (zipInfo?.paths || []).find((p) => /(^|\/)manifest\.json(\.gz)?$/i.test(String(p)))
    || (/manifest\.json(\.gz)?$/i.test(String(issue?.sourceModel?.path || "")) ? issue.sourceModel.path : "")
    || ""
  );
  // A plugin resolution points the issue at the project manifest without
  // a zipInfo listing, so "is there something to stream" is a question
  // about the manifest OR the glb list - not the zip alone.
  // What is actually on screen: the preview if one is chosen, otherwise
  // the issue's own source.
  // While a resolution is under review the pane shows the push that
  // carried the fix, not the version the issue was pinned to: painting
  // "moved" onto a model in which nothing has moved yet reads as a lie.
  const reviewManifest = review && review.active && review.manifestPath ? String(review.manifestPath) : "";
  const activeManifest = preview?.manifestPath || reviewManifest || manifestPath;
  const previewing = !!(preview && preview.manifestPath && preview.manifestPath !== manifestPath);
  const hasStreamable = !!(activeManifest || (zipInfo?.paths || []).some((p) => /\.glb(\.gz)?$/i.test(String(p))));
  const modelKey = `${issue?._id || ""}|${issue?.sourceModel?.timelineId || ""}|${issue?.sourceModel?.fromIssueId || ""}|${activeManifest}|${(zipInfo?.paths || []).length}`;

  // The revision the rail should describe, and the model should be
  // painted with. Three ways one arrives, in order of immediacy:
  // the version the reader is previewing, the resolution the page is
  // reviewing, and the push the issue itself was raised against.
  const diffCommitId = revisionOnScreen({ previewing, preview, review, issue });
  // The page hands the resolution diff down ready-painted; a commit the
  // reader picked (or the issue's own source push) has to be fetched
  // here, or the rail would say what changed while the model did not.
  const [ownDiff, setOwnDiff] = useState(null);
  const needOwnDiff = !!diffCommitId && !(review && review.active && String(review.commitId) === diffCommitId);
  useEffect(() => {
    let alive = true;
    if (!needOwnDiff) { setOwnDiff(null); return undefined; }
    getCommitDiff(diffCommitId, { limit: 4000 })
      .then((r) => {
        const body = r && r.data ? r.data : r;
        if (alive) setOwnDiff(body && body.elements ? body : null);
      })
      .catch(() => { if (alive) setOwnDiff(null); });
    return () => { alive = false; };
  }, [diffCommitId, needOwnDiff]);
  // One review object for the rest of the component, whichever lane it
  // came from.
  const paint = useMemo(() => {
    if (review && review.active) return review;
    if (ownDiff) {
      return {
        active: true,
        commitId: diffCommitId,
        elements: ownDiff.elements || [],
        summary: ownDiff.summary || {},
        changedTilesByCategory: ownDiff.changedTilesByCategory || {},
      };
    }
    return null;
  }, [review, ownDiff, diffCommitId]);

  // ── viewer lifecycle ────────────────────────────────────────────────
  useEffect(() => () => { if (activeIssueViewer.current === WebGL.current) activeIssueViewer.current = null; }, []);

  useEffect(() => {
    const el = viewerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => {
      try {
        const fn = WebGL.current?.viewer?.onWindowResize;
        if (typeof fn === "function") fn.call(WebGL.current.viewer);
      } catch (e) { /* noop */ }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!issue?._id || !hasStreamable) return undefined;
    if (!viewerRef.current) return undefined;

    let cancelled = false;
    let retryTimer = null;

    const init = () => {
      if (cancelled || !viewerRef.current) return;
      const rect = viewerRef.current.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) {
        retryTimer = setTimeout(init, 150);
        return;
      }
      const glbUrls = ((zipInfo && zipInfo.paths) || [])
        .filter((p) => /\.glb(\.gz)?$/i.test(String(p)))
        .map((p) => `${url}/${String(p).replace(/^\/+/, "")}`);
      const manifestUrl = activeManifest
        ? `${url}/${String(activeManifest).replace(/^\/+/, "")}`
        : null;
      if (!glbUrls.length && !manifestUrl) {
        setErrText("No viewable model files in this export.");
        return;
      }
      try {
        WebGL.current = new WebGlApp(
          viewerRef.current,
          manifestUrl ? { glbs: glbUrls, manifestUrl } : { glbs: glbUrls },
          {
            setLoading: (loading) => { if (!loading && !cancelled) setIsGLLoaded(true); },
            // The loader reports failure as setError(true) (webgl/js/loader/
            // streaming/streamingLoader.js), and String(true) painted a bare
            // "true" in the middle of the pane where the model should be.
            setError: (e) => {
              if (cancelled) return;
              const text = e && typeof e === "object" && e.message ? String(e.message)
                : (typeof e === "string" && e ? e : "");
              setErrText(text || "The 3D model could not be loaded. Reload the page, or pick another model source above.");
            },
            setPercentsLoaded: (p) => { if (!cancelled && p <= 100) setPct(p); },
            setAllowToResize: () => {},
            setModal: () => {},
            setIsPopOver: () => {},
            onHoverMesh: () => {},
            setCoordsForPopover: () => {},
            // SECOND ARGUMENT: the viewer passes e.ctrlKey through here (see
            // webgl/js/viewer/index.js, the canvas click handler). This
            // handler used to declare ONE parameter and drop it, so a
            // ctrl-click arrived indistinguishable from a plain click and the
            // line below replaced the whole selection with the clicked
            // element every time. That is why ctrl-click could not add or
            // remove on this page while it worked everywhere else: not
            // different logic - DISCARDED INPUT.
            //
            // selectUid already does what the main viewer does - plain
            // replaces, additive toggles - and applies the highlight through
            // the viewer's own onClickChildInList. The tree has always used
            // it; the canvas now uses the same function instead of a second,
            // lesser copy of it.
            //
            // Through a ref because this ReactApi is built once at mount and
            // would otherwise close over the first render's selectUid, and
            // with it an empty selection that never grows.
            findInJsonUsingViewer: (uniqueId, isCtrl) => {
              if (!uniqueId) return;
              if (pinModeRef.current) { setPendingPinUniqueId(uniqueId); return; }
              // Not the optional-call form: this eslint config reads
              // `fn?.(args)` as an unused expression and fails the build.
              const select = selectUidRef.current;
              if (select) select(uniqueId, !!isCtrl);
            },
            setClickedChild: () => {},
            selectedMesh: { current: null },
            project: issue.projectId,
          },
          null,
          false,
          isIfcProject
        );
        attachBuildingGrid(WebGL.current.viewer, projectId);
        activeIssueViewer.current = WebGL.current;
      } catch (e) {
        setErrText(String(e?.message || e));
      }
    };

    const boot = setTimeout(init, 80);
    return () => {
      cancelled = true;
      clearTimeout(boot);
      clearTimeout(retryTimer);
      try {
        if (WebGL.current?.loader?.dispose) WebGL.current.loader.dispose();
        if (WebGL.current?.viewer?.destroy) WebGL.current.viewer.destroy();
        if (WebGL.current?.viewer?.renderer?.dispose) WebGL.current.viewer.renderer.dispose();
      } catch (e) { /* noop */ }
      WebGL.current = null;
      setIsGLLoaded(false);
      setPct(0);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelKey]);

  // ── the resolution's diff, painted on the loaded model ──────────────
  const reviewKey = `${paint?.active ? "on" : "off"}|${paint?.commitId || ""}|${(paint?.elements || []).length}`;
  useEffect(() => {
    if (!isGLLoaded || !paint?.active) return undefined;
    const v = WebGL.current?.viewer;
    if (!v) return undefined;
    const cleanup = applyDiffOverlay(v, paint.elements, paint.changedTilesByCategory);
    return cleanup || undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGLLoaded, reviewKey]);

  // ── tree ────────────────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    if (!projectId) return undefined;
    setTree(null);
    setTreeErr("");
    getModelTree(projectId, {
      manifestPath: activeManifest || undefined,
      // With a revision on screen the rail is a REVIEW tree: every node
      // reports what this push did to it.
      diffCommitId: diffCommitId || undefined,
    })
      .then((r) => {
        if (!alive) return;
        const body = r && r.data ? r.data : r;
        if (!body || body.err) {
          setTree([]);
          setTreeErr(body?.err || "This source has no browsable index.");
          return;
        }
        setTree(body.categories || []);
        setTreeSource(body.source || null);
        setTreeChange(body.change || null);
      })
      .catch((e) => {
        if (!alive) return;
        setTree([]);
        setTreeErr(e?.err || e?.message || "Could not read the model index");
      });
    return () => { alive = false; };
  }, [projectId, activeManifest, diffCommitId]);

  const store = () => {
    const v = WebGL.current?.viewer;
    return v?.streaming?.state || v?.stateStore || null;
  };

  const loadNodeElements = useCallback(async (categoryKey, typeKey) => {
    const key = typeKey ? `${categoryKey}|${typeKey}` : categoryKey;
    if (elementsBy[key]) return elementsBy[key];
    setLoadingNode(key);
    try {
      const r = await getModelTree(projectId, {
        manifestPath: activeManifest || undefined,
        category: categoryKey, type: typeKey || undefined, limit: 500,
        diffCommitId: diffCommitId || undefined,
      });
      const body = r && r.data ? r.data : r;
      const list = body?.elements || [];
      setElementsBy((prev) => ({ ...prev, [key]: list }));
      return list;
    } catch (e) {
      message.error(e?.err || e?.message || "Could not load the elements");
      return [];
    } finally {
      setLoadingNode("");
    }
  }, [elementsBy, projectId, activeManifest, diffCommitId]);

  // uids of a node, for show/hide of a whole category or type
  const nodeUids = useCallback(async (categoryKey, typeKey) => {
    const r = await getModelTree(projectId, {
      manifestPath: activeManifest || undefined,
      category: categoryKey, type: typeKey || undefined, mode: "uids",
    });
    const body = r && r.data ? r.data : r;
    return body?.uids || [];
  }, [projectId, activeManifest]);

  const toggleNodeVisible = async (categoryKey, typeKey) => {
    const key = typeKey ? `${categoryKey}|${typeKey}` : categoryKey;
    const s = store();
    if (!s) return;
    const uids = await nodeUids(categoryKey, typeKey);
    if (!uids.length) return;
    const nowHidden = !hiddenNodes[key];
    // Group-scoped hide so turning one trade back on doesn't reveal
    // elements another group is still hiding.
    if (nowHidden) {
      if (s.hideGroup) s.hideGroup(key, uids);
      else if (s.bulkHide) s.bulkHide(uids);
    } else if (s.showGroup) s.showGroup(key);
    else if (s.bulkShow) s.bulkShow(uids);
    setHiddenNodes((prev) => ({ ...prev, [key]: nowHidden }));
  };

  const selectUid = (uid, additive) => {
    const v = WebGL.current?.viewer;
    setSelection((prev) => {
      const next = additive
        ? (prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid])
        : [uid];
      return next;
    });
    try {
      // The viewer keys its "previously selected" bookkeeping off the
      // NAMES in `selectors` (it stores the arrays on itself under those
      // keys) and off treeIndex, so this canvas needs its own pair or it
      // would fight the model-view page's trees over the same fields.
      if (v?.onClickChildInList) {
        v.onClickChildInList(uid, !!additive, {
          prevMeshes: "prevIssueCanvasMeshes",
          prevMaterials: "prevIssueCanvasMaterials",
          color: "green",
          treeIndex: "issue-canvas",
        }, false, !additive);
      }
    } catch (e) { /* selection is a nicety, never fatal */ }
  };
  // Written every render so the mount-time ReactApi always calls the
  // CURRENT selectUid, and therefore sees the current selection.
  selectUidRef.current = selectUid;

  // ── toolbar ─────────────────────────────────────────────────────────
  const viewer = () => WebGL.current?.viewer;

  const onHideSelected = () => {
    const s = store();
    if (!s || selection.length === 0) return;
    if (s.bulkHide) s.bulkHide(selection);
    setSelection([]);
  };
  const onShowAll = () => {
    const s = store();
    if (!s) return;
    if (s.showAll) s.showAll();
    if (s.clearIsolation) s.clearIsolation();
    if (s.clearDisabled) s.clearDisabled();
    setHiddenNodes({});
  };
  // "Ghost the rest": everything except the selection drops back, which
  // is isolate-without-losing-context — the same treatment a resolution
  // diff gets.
  const onGhostOthers = () => {
    const s = store();
    const v = viewer();
    if (!s || !v || selection.length === 0) return;
    try {
      const uids = v.streaming?.enumerateActiveUids ? v.streaming.enumerateActiveUids() : [];
      const keep = new Set(selection);
      const ghost = uids.filter((u) => !keep.has(u));
      if (ghost.length && s.bulkDisable) s.bulkDisable(ghost);
    } catch (e) { /* best-effort */ }
  };

  // ── the model's versions ────────────────────────────────────────────
  //
  // Every push, model version and issue export the project has, loadable
  // into THIS canvas. Fetched when the panel is first opened - it is a
  // few hundred rows of metadata and nobody needs it to look at a pin.
  useEffect(() => {
    let alive = true;
    if (!sourcesOpen || sources || !issue?._id) return undefined;
    getIssueModelSources(issue._id)
      .then((r) => { if (alive) setSources(r && r.data ? r.data : r); })
      .catch(() => { if (alive) setSources({ commits: [], timelines: [], issues: [] }); });
    return () => { alive = false; };
  }, [sourcesOpen, sources, issue?._id]);

  // Load a version into the canvas WITHOUT changing the issue. Everything
  // resets: the viewer remounts on the new manifest, the tree is rebuilt
  // for it, and any selection or hidden set belonged to the old one.
  const previewVersion = useCallback((next) => {
    setPreview(next);
    setSelection([]);
    setElementsBy({});
    setExpanded({});
    setHiddenNodes({});
    setChangedOnly(false);
  }, []);

  // Make the version on screen the issue's own 3D source.
  const attachVersion = useCallback(async (row) => {
    if (!issue?._id || !row) return;
    setAttaching(row.key || "1");
    try {
      const body = row.kind === "commit" ? { commitId: row.commitId }
        : row.kind === "timeline" ? { timelineId: row.id }
          : { fromIssueId: row.id };
      const r = await setIssueSourceModel(issue._id, body);
      if (r && r.err) throw new Error(r.err);
      message.success(`This issue now points at ${row.label}`);
      setPreview(null);
      setSourcesOpen(false);
      if (onIssueUpdated) onIssueUpdated();
    } catch (e) {
      message.error(e?.message || "Could not attach that version");
    } finally {
      setAttaching("");
    }
  }, [issue?._id, onIssueUpdated]);

  // The three lanes as ONE list, newest-first within each: the model's
  // own pushes (what the plugin synced), the project's model versions,
  // and the elements other issues were exported with. A row that cannot
  // be streamed still lists, with the reason - an older push whose
  // export was overwritten is a fact about the project's history, not an
  // omission.
  const versionRows = useMemo(() => {
    if (!sources) return [];
    const cur = issue?.sourceModel || {};
    const rows = [];
    for (const c of sources.commits || []) {
      rows.push({
        key: `commit:${c.commitId}`,
        kind: "commit",
        group: "MODEL PUSHES",
        commitId: c.commitId,
        label: c.title + (c.kind === "selection" ? " (selection)" : ""),
        when: c.ts ? moment(c.ts).format("MMM D, YYYY · h:mm A") : "",
        by: c.by || "",
        change: c.counts,
        narrative: c.narrative || "",
        manifestPath: c.manifestPath || "",
        live: !!c.live,
        streamable: !!c.streamable,
        why: "This push predates versioned exports - its geometry was overwritten by the next one.",
        isSource: String(cur.commitId || "") === String(c.commitId),
      });
    }
    for (const t of sources.timelines || []) {
      rows.push({
        key: `timeline:${t._id}`,
        kind: "timeline",
        group: "MODEL VERSIONS",
        id: t._id,
        label: [t.revision ? `R${t.revision.revisionNumber}` : "", t.title].filter(Boolean).join(" · ") || "Model version",
        when: t.date ? moment(t.date).format("MMM D, YYYY") : "",
        by: "",
        manifestPath: t.manifestPath || "",
        streamable: !!t.streamable,
        why: "",
        isSource: String(cur.timelineId || "") === String(t._id),
      });
    }
    for (const i of sources.issues || []) {
      rows.push({
        key: `issue:${i._id}`,
        kind: "issue",
        group: "EXPORTED AGAINST AN ISSUE",
        id: i._id,
        label: `${i.displayId ? `#${i.displayId} ` : ""}${i.title || i.name || "Issue export"}${i.isThisIssue ? " (this issue)" : ""}`,
        when: i.exportedAt ? moment(i.exportedAt).format("MMM D, YYYY") : "",
        by: "",
        manifestPath: i.manifestPath || "",
        streamable: !!i.streamable,
        why: "",
        isSource: String(cur.fromIssueId || "") === String(i._id) || (!!i.isThisIssue && !cur.timelineId && !cur.fromIssueId && !cur.commitId),
      });
    }
    return rows;
  }, [sources, issue?.sourceModel]);

  const filteredTree = useMemo(() => {
    if (!tree) return null;
    const needle = q.trim().toLowerCase();
    const onlyChanged = changedOnly && !!treeChange;
    if (!needle && !onlyChanged) return tree;
    return tree
      .map((c) => {
        if (onlyChanged && !nodeTouched(c.change)) return null;
        const catHit = !needle || c.label.toLowerCase().includes(needle);
        const types = (c.types || []).filter((t) => {
          if (onlyChanged && !nodeTouched(t.change) && !nodeTouched(c.change)) return false;
          return catHit || t.label.toLowerCase().includes(needle);
        });
        return catHit || types.length ? { ...c, types } : null;
      })
      .filter(Boolean);
  }, [tree, q, changedOnly, treeChange]);

  // ── the pinned markup view ──────────────────────────────────────────
  //
  // `markupView` is the camera the issue's markup was drawn at (see
  // PUT /issue/:id/markup-view). The overlay is shown only while the
  // camera is actually there: orbit and the drawing drops away, because
  // a line drawn over a door at one angle is over a wall at another.
  // "Reset to the marked-up view" flies back and brings it with it.
  const [atPinned, setAtPinned] = useState(false);
  const [overlayOn, setOverlayOn] = useState(true);
  const [pinning, setPinning] = useState(false);
  const pinnedRef = useRef(null);       // { position:[x,y,z], target:[x,y,z] }
  const markupView = issue?.markupView && issue.markupView.camera ? issue.markupView : null;
  // A pinned view belongs to the model it was drawn on.
  const pinnedMatchesModel = !!markupView
    && (!markupView.manifestPath || markupView.manifestPath === activeManifest);
  const markupSrc = markupView && markupView.screenshotUrl
    ? (String(markupView.screenshotUrl).startsWith("/") ? `${url}${markupView.screenshotUrl}` : markupView.screenshotUrl)
    : "";

  const cameraControls = () => WebGL.current?.viewer?.cameraControls || null;

  // The viewpoint a plugin captured: fly to it on first load unless a human
  // pinned a view here, and mark the elements it was about (hooks/useViewpointCamera).
  const viewpoint = useViewpointCamera({
    issue,
    isGLLoaded,
    cameraControls,
    store,
    pinnedView: !!(markupView && pinnedMatchesModel),
    viewer: () => WebGL.current?.viewer || null,
    projectId: String(projectId || ""),
  });

  // Where the camera is, in the two numbers that decide "same view".
  const readCamera = useCallback(() => {
    const cc = cameraControls();
    if (!cc) return null;
    try {
      const p = cc.getPosition ? cc.getPosition(new Vector3()) : null;
      const t = cc.getTarget ? cc.getTarget(new Vector3()) : null;
      if (!p || !t) return null;
      return { position: [p.x, p.y, p.z], target: [t.x, t.y, t.z] };
    } catch (e) { return null; }
  }, []);

  // Restore the pinned camera and, once it has arrived, the markup.
  const goToPinnedView = useCallback(() => {
    const cc = cameraControls();
    if (!cc || !markupView?.camera) return;
    try {
      cc.fromJSON(markupView.camera, true);   // true = animate
      // Hidden / isolated sets are part of the picture, not decoration.
      const st = store();
      if (st) {
        if (st.showAll) st.showAll();
        if (st.setIsolation) st.setIsolation(null);
        const hide = markupView.hiddenElements || [];
        if (hide.length && st.bulkHide) st.bulkHide(hide);
        const iso = markupView.isolatedElements || [];
        if (iso.length && st.setIsolation) st.setIsolation(iso);
      }
      setOverlayOn(true);
    } catch (e) {
      message.error("The saved view could not be restored");
    }
  }, [markupView]);

  // Am I looking at the pinned view? Compared rather than event-driven:
  // camera-controls fires for transitions, damping and user input alike,
  // and the only thing that matters here is where the camera ENDED UP.
  useEffect(() => {
    if (!isGLLoaded || !markupView?.camera || !pinnedMatchesModel) { setAtPinned(false); return undefined; }
    // The pinned position, read once by restoring it into a throwaway
    // copy of the state - no maths against camera-controls' JSON shape.
    try {
      const parsed = JSON.parse(markupView.camera);
      const p = parsed.position || parsed.p;
      const t = parsed.target || parsed.t;
      if (Array.isArray(p) && Array.isArray(t)) pinnedRef.current = { position: p, target: t };
      else if (p && t) pinnedRef.current = { position: [p.x, p.y, p.z], target: [t.x, t.y, t.z] };
    } catch (e) { pinnedRef.current = null; }

    const near = (a, b, eps) => Math.abs(a - b) <= eps;
    const id = setInterval(() => {
      const now = readCamera();
      const want = pinnedRef.current;
      if (!now || !want) { setAtPinned(false); return; }
      // The tolerance scales with how far away the camera is, so "close
      // enough" means the same thing on a corridor detail and a site plan.
      const dist = Math.hypot(
        now.position[0] - now.target[0], now.position[1] - now.target[1], now.position[2] - now.target[2]
      ) || 1;
      const eps = Math.max(0.05, dist * 0.004);
      const same = want.position.every((v, i) => near(v, now.position[i], eps))
        && want.target.every((v, i) => near(v, now.target[i], eps));
      setAtPinned(same);
    }, 220);
    return () => clearInterval(id);
  }, [isGLLoaded, markupView?.camera, pinnedMatchesModel, readCamera]);

  // Pin the view the reader has composed. The raster is captured by the
  // page (Screenshot for markup); this records where it was taken from,
  // so the two halves of a markup - the drawing and the angle - are
  // saved together or not at all.
  const pinCurrentView = useCallback(async ({ screenshotUrl = "", screenshotVersion = 0 } = {}) => {
    const cc = cameraControls();
    if (!cc || !issue?._id) return null;
    setPinning(true);
    try {
      const st = store();
      const body = {
        camera: cc.toJSON(),
        fov: WebGL.current?.viewer?.camera?.fov || 0,
        aspect: WebGL.current?.viewer?.camera?.aspect || 0,
        screenshotUrl,
        screenshotVersion,
        // The store keeps these as Sets on the instance - `hidden` is the
        // union of everything hidden by hand or by a group toggle, and
        // `isolated` is null when nothing is isolated.
        hiddenElements: st && st.hidden ? [...st.hidden] : [],
        isolatedElements: st && st.isolated ? [...st.isolated] : [],
        manifestPath: activeManifest || "",
        commitId: diffCommitId || undefined,
      };
      const r = await setIssueMarkupView(issue._id, body);
      if (r && r.err) throw new Error(r.err);
      if (onIssueUpdated) onIssueUpdated();
      return r;
    } catch (e) {
      message.error(e?.message || "Could not pin this view");
      return null;
    } finally {
      setPinning(false);
    }
  }, [issue?._id, activeManifest, diffCommitId, onIssueUpdated]);

  // The page's "Screenshot for markup" needs the camera at the moment it
  // captures, so the canvas publishes its controls alongside the viewer.
  useEffect(() => {
    if (!isGLLoaded) return undefined;
    activeIssueViewer.pinView = pinCurrentView;
    activeIssueViewer.readCamera = readCamera;
    return () => {
      if (activeIssueViewer.pinView === pinCurrentView) activeIssueViewer.pinView = null;
    };
  }, [isGLLoaded, pinCurrentView, readCamera]);

  const shellStyle = {
    position: "relative",
    display: "flex",
    background: "#1A1A1A",
    border: "1px solid #0C0C0C",
    borderRadius: 6,
    overflow: "hidden",
    ...(fill ? { flex: 1, minHeight: 0, width: "100%" } : { height: height || 460 }),
  };

  if (!hasStreamable) {
    return (
      <div style={{ ...shellStyle, alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,.6)", fontSize: 12, padding: 20, textAlign: "center" }}>
        No 3D model attached to this issue yet — pick a model version or a
        plugin export in the <b style={{ color: "#fff" }}>3D MODEL</b> selector above.
      </div>
    );
  }

  return (
    <div style={shellStyle}>
      {/* ── tree rail ─────────────────────────────────────────────── */}
      {railOpen && (
        <div style={{
          width: RAIL_WIDTH, flexShrink: 0, display: "flex", flexDirection: "column",
          background: "#15181B", borderRight: "1px solid #0C0C0C", color: "#D7DDE2",
        }}>
          <div style={{ padding: "8px 10px", borderBottom: "1px solid #23282C" }}>
            <Input
              size="small"
              allowClear
              prefix={<SearchOutlined style={{ color: "#7C8B96" }} />}
              placeholder="Find a category or type"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            {treeSource && (
              <div style={{ fontSize: 10.5, color: "#7C8B96", marginTop: 6, lineHeight: 1.35 }}>
                {treeSource.elements} elements · {treeSource.tilesRead} tile{treeSource.tilesRead === 1 ? "" : "s"}
                {treeSource.truncated ? ` (first ${treeSource.tilesRead} of ${treeSource.tiles})` : ""}
              </div>
            )}
            {/* With a revision on screen the rail states what it did,
                and can be narrowed to just those rows - the tree and the
                painted model then answer the same question. */}
            {treeChange && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 7, flexWrap: "wrap" }}>
                <NodeDelta change={treeChange.totals} />
                <span
                  onClick={() => setChangedOnly((v) => !v)}
                  style={{
                    cursor: "pointer", fontSize: 10, padding: "2px 6px", borderRadius: 3,
                    border: `1px solid ${changedOnly ? "#FE6919" : "#2B3238"}`,
                    background: changedOnly ? "rgba(254,105,25,.18)" : "transparent",
                    color: changedOnly ? "#FFB98C" : "#7C8B96", whiteSpace: "nowrap",
                  }}
                >
                  changed only
                </span>
                {treeChange.unplaced > 0 && (
                  <Tooltip title="Deleted elements are not in this export, so they could not be placed under a type. They are counted on their category.">
                    <span style={{ fontSize: 10, color: "#7C8B96", cursor: "help" }}>
                      {treeChange.unplaced} unplaced
                    </span>
                  </Tooltip>
                )}
              </div>
            )}
          </div>

          <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "4px 0" }}>
            {tree === null && (
              <div style={{ padding: 16, textAlign: "center" }}><Spin size="small" /></div>
            )}
            {tree !== null && filteredTree?.length === 0 && (
              <div style={{ padding: "12px 10px", fontSize: 11.5, color: "#7C8B96" }}>
                {treeErr || "Nothing matches that."}
              </div>
            )}
            {(filteredTree || []).map((c) => {
              const open = !!expanded[c.key];
              const catHidden = !!hiddenNodes[c.key];
              return (
                <div key={c.key}>
                  <div style={{
                    display: "flex", alignItems: "center", gap: 6, padding: "5px 8px",
                    cursor: "pointer", fontSize: 12, opacity: catHidden ? 0.45 : 1,
                  }}>
                    <span
                      onClick={() => setExpanded((p) => ({ ...p, [c.key]: !open }))}
                      style={{ width: 12, color: "#7C8B96", flexShrink: 0 }}
                    >
                      {open ? <DownOutlined style={{ fontSize: 9 }} /> : <RightOutlined style={{ fontSize: 9 }} />}
                    </span>
                    <span
                      onClick={() => setExpanded((p) => ({ ...p, [c.key]: !open }))}
                      style={{ flex: 1, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                      title={c.label}
                    >
                      {c.label}
                    </span>
                    <span style={{ fontSize: 10.5, color: "#7C8B96" }}>{c.count}</span>
                    {c.change && <NodeDelta change={c.change} dim={catHidden} />}
                    <Tooltip title={catHidden ? "Show this category" : "Hide this category"}>
                      <span
                        onClick={(e) => { e.stopPropagation(); toggleNodeVisible(c.key); }}
                        style={{ color: catHidden ? "#7C8B96" : "#D7DDE2", flexShrink: 0 }}
                      >
                        {catHidden ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                      </span>
                    </Tooltip>
                  </div>

                  {open && (c.types || []).map((t) => {
                    const tKey = `${c.key}|${t.key}`;
                    const tOpen = !!expanded[tKey];
                    const tHidden = !!hiddenNodes[tKey];
                    const els = elementsBy[tKey];
                    return (
                      <div key={tKey}>
                        <div style={{
                          display: "flex", alignItems: "center", gap: 6,
                          padding: "4px 8px 4px 22px", fontSize: 11.5, cursor: "pointer",
                          opacity: tHidden ? 0.45 : 1,
                        }}>
                          <span
                            onClick={() => {
                              setExpanded((p) => ({ ...p, [tKey]: !tOpen }));
                              if (!tOpen && !els) loadNodeElements(c.key, t.key);
                            }}
                            style={{ width: 12, color: "#7C8B96", flexShrink: 0 }}
                          >
                            {tOpen ? <DownOutlined style={{ fontSize: 9 }} /> : <RightOutlined style={{ fontSize: 9 }} />}
                          </span>
                          <span
                            onClick={() => {
                              setExpanded((p) => ({ ...p, [tKey]: !tOpen }));
                              if (!tOpen && !els) loadNodeElements(c.key, t.key);
                            }}
                            style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                            title={t.label}
                          >
                            {t.label}
                          </span>
                          <span style={{ fontSize: 10.5, color: "#7C8B96" }}>{t.count}</span>
                          {t.change && <NodeDelta change={t.change} dim={tHidden} />}
                          <Tooltip title={tHidden ? "Show this type" : "Hide this type"}>
                            <span
                              onClick={(e) => { e.stopPropagation(); toggleNodeVisible(c.key, t.key); }}
                              style={{ color: tHidden ? "#7C8B96" : "#D7DDE2", flexShrink: 0 }}
                            >
                              {tHidden ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                            </span>
                          </Tooltip>
                        </div>

                        {tOpen && loadingNode === tKey && (
                          <div style={{ padding: "4px 0 4px 36px" }}><Spin size="small" /></div>
                        )}
                        {tOpen && (els || [])
                          .filter((e) => !(changedOnly && treeChange && !e.change))
                          .map((e) => {
                          const on = selection.includes(e.uniqueId);
                          return (
                            <div
                              key={e.uniqueId}
                              onClick={(ev) => selectUid(e.uniqueId, ev.ctrlKey || ev.metaKey)}
                              title={`${e.name || e.uniqueId}${e.level ? ` · ${e.level}` : ""}${
                                e.change ? ` · ${ELEMENT_MARK[e.change]?.label || e.change}` : ""}`}
                              style={{
                                display: "flex", alignItems: "center", gap: 5,
                                padding: "3px 8px 3px 27px", fontSize: 11, cursor: "pointer",
                                background: on ? "rgba(254,105,25,.22)" : "transparent",
                                color: on ? "#fff" : "#B9C3CB",
                              }}
                            >
                              {treeChange ? <ElementMark change={e.change} /> : null}
                              <span style={{ flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {e.name || e.uniqueId.slice(0, 12)}
                                {e.level ? <span style={{ color: "#7C8B96" }}> · {e.level}</span> : null}
                              </span>
                            </div>
                          );
                        })}
                        {tOpen && els && els.length === 0 && (
                          <div style={{ padding: "3px 8px 3px 36px", fontSize: 11, color: "#7C8B96" }}>No elements</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── canvas ────────────────────────────────────────────────── */}
      <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
        <div ref={viewerRef} style={{ position: "absolute", inset: 0, background: "#1A1A1A" }} />

        <div style={{
          position: "absolute", top: 8, left: 8, display: "flex", gap: 4, flexWrap: "wrap",
          background: "rgba(0,0,0,.55)", padding: 4, borderRadius: 4, zIndex: 5, maxWidth: "80%",
        }}>
          <ToolBtn title={railOpen ? "Hide the model tree" : "Show the model tree"}
                   icon={railOpen ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
                   onClick={() => setRailOpen((v) => !v)} />
          <ToolBtn title="Versions - load any push, revision or export of this model"
                   active={sourcesOpen || previewing}
                   icon={<HistoryOutlined />}
                   onClick={() => setSourcesOpen((v) => !v)} />
          {/* THE VIEWER'S OWN TOOLS ARE NOT REPEATED HERE.
              Zoom, frame, home, isolate, hide, unhide, grid, measure,
              section, screenshot and the effects gear are the DEFAULT
              toolbar's (components/Viewer/ViewerToolbar), rendered at the
              bottom of this canvas exactly as every other embedded viewer
              renders it. This rail keeps only what belongs to the ISSUE
              page: the model tree, the version picker, the marked-up view
              and pin mode.
              The duplicates were not merely redundant - the viewer resolves
              its own tool buttons by GLOBAL element id (gridToggleBtn,
              measurementToggleBtn, clippingToggleBtn, isolateToggleBtn), so
              a private copy under a different id left the tool running while
              its button never lit, which reads as a dead button. */}
          {markupView && pinnedMatchesModel && (
            <ToolBtn
              title={atPinned ? "You are at the marked-up view" : "Go back to the marked-up view"}
              active={atPinned}
              icon={<PushpinOutlined />}
              onClick={goToPinnedView}
            />
          )}
          <ViewpointIsolateButton viewpoint={viewpoint} Btn={ToolBtn} />
          <ToolBtn title="Ghost everything else" icon={<ColumnWidthOutlined />} onClick={onGhostOthers} />
          {/* The view strip moved to its own top-right column, below
              the selection panel. This toolbar is top-LEFT and holds
              the page's own stateful controls. */}
          {canEdit && (
            <ToolBtn
              title={pinMode ? "Pin mode ON — click an element to pin the issue to it" : "Place a pin"}
              active={pinMode}
              icon={<PlusOutlined />}
              onClick={() => setPinMode((v) => !v)}
            />
          )}
        </div>

        {/* TOP RIGHT, AS A COLUMN: the selection / diff panel first, the
            view strip beneath it (owner via leader, 2026-09-20).
            A column rather than two boxes with offsets, because the panel
            is CONDITIONAL and its height is dynamic - anything that put
            the strip at a fixed `top` would either overlap the panel when
            it appears or float below nothing when it does not. */}
        <div style={{
          position: "absolute", top: 8, right: 8, zIndex: 6,
          display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8,
        }}>
        {(selection.length > 0 || paint?.active) && (
          <div style={{
            width: 210,
            background: "rgba(0,0,0,.62)", borderRadius: 6, padding: "8px 10px", color: "#fff", fontSize: 11,
          }}>
            {paint?.active && (
              <>
                <div style={{ fontWeight: 700, letterSpacing: ".05em", fontSize: 10, opacity: .75, marginBottom: 6 }}>
                  3D CHANGE
                </div>
                {diffLegendRows(paint.summary).map((r) => (
                  <div key={r.key} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: r.color, flexShrink: 0 }} />
                    <span style={{ flex: 1, opacity: .85 }}>{r.label}</span>
                    <b>{r.count}</b>
                  </div>
                ))}
                {paint.onApprove && (
                  <div style={{ display: "flex", gap: 6, margin: "8px 0" }}>
                    <Button size="small" type="primary" style={{ flex: 1 }} loading={paint.busy === "approve"}
                            onClick={paint.onApprove}>Approve</Button>
                    <Button size="small" danger style={{ flex: 1 }} loading={paint.busy === "reject"}
                            onClick={paint.onReject}>Send back</Button>
                  </div>
                )}
              </>
            )}
            {selection.length > 0 && (
              <div style={{ marginTop: paint?.active ? 6 : 0, paddingTop: paint?.active ? 6 : 0, borderTop: paint?.active ? "1px solid rgba(255,255,255,.15)" : "none" }}>
                <div style={{ fontWeight: 700, letterSpacing: ".05em", fontSize: 10, opacity: .75, marginBottom: 4 }}>
                  SELECTION ({selection.length})
                </div>
                <div style={{ opacity: .85, wordBreak: "break-all", lineHeight: 1.35 }}>
                  {selection.slice(0, 3).map((u) => <div key={u}>{u.slice(0, 24)}…</div>)}
                  {selection.length > 3 ? <div>+{selection.length - 3} more</div> : null}
                </div>
                <Button size="small" style={{ marginTop: 6, width: "100%" }} onClick={() => { setSelection([]); onShowAll(); }}>
                  Clear
                </Button>
              </div>
            )}
          </div>
        )}
          <ViewerViewButtons viewer={() => WebGL.current && WebGL.current.viewer} variant="dark" />
          {/* The same toolbar the model view has - same icons, same
              place - one appearance per command (owner, 2026-09-23). */}
            {/* The gear is the toolbar's again. It was turned OFF here
                because this page rendered its own ViewerEffectsButton and
                two gears would have been the very duplication that change
                was removing. That button is gone now, so there is exactly
                one gear and it is the shared one. */}
        </div>

        {/* THE NATIVE TOOLBAR IS A SIBLING OF THE PANELS, NOT A CHILD.

            MEASURED TWICE, because my first attempt at this was wrong and
            the drive caught it. The toolbar was nested inside the TOP-RIGHT
            COLUMN above - position absolute, top 8, right 8 - which is 20px
            tall and pinned to the top. So viewer-zoom's bottom:56px measured
            up from THAT, and the buttons landed at y=509, above the canvas
            (which starts at 561) and underneath the page header, where
            elementFromPoint returned the header and not the button.
            Giving that wrapper inset:0 only made it fill the 20px column:
            the buttons moved to y=518. A nine-pixel improvement on a
            fifty-two pixel problem, which is what fixing the wrong box
            looks like.

            Here it is a direct child of the RELATIVE canvas container, so
            "56px up from the bottom" means 56px up from the bottom of the
            viewer - the same thing it means on every other page. The panels
            keep their own corner and no longer decide where the native
            controls go. */}
        <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, top: 0,
           zIndex: 5, pointerEvents: "none" }}>
          <div style={{ pointerEvents: "auto" }}>
            <ViewerToolbar viewer={() => WebGL.current && WebGL.current.viewer} />
          </div>
        </div>

        {/* ── the markup overlay ─────────────────────────────────────
            The issue's marked-up picture, laid back over the live model
            at the camera it was drawn from. It is shown ONLY while the
            camera is there: the first orbit drops it, because an arrow
            that pointed at a duct from one angle points at nothing from
            another. The pin button flies back and brings it with it, so
            the view is never lost - the model stays rotatable and the
            annotation stays true. */}
        {markupSrc && pinnedMatchesModel && atPinned && overlayOn && (
          <div style={{ position: "absolute", inset: 0, zIndex: 3, pointerEvents: "none" }}>
            <img
              src={markupSrc}
              alt="Markup"
              style={{
                position: "absolute", inset: 0, width: "100%", height: "100%",
                objectFit: "contain", opacity: 0.92,
                // The markup is drawn on white; multiply lets the model
                // show through the paper and keeps the ink solid.
                mixBlendMode: "multiply",
              }}
            />
          </div>
        )}
        {markupSrc && pinnedMatchesModel && (
          <div style={{
            position: "absolute", bottom: 10, left: 10, zIndex: 6, display: "flex", gap: 6,
            alignItems: "center", background: "rgba(0,0,0,.6)", borderRadius: 6, padding: "5px 9px",
            color: "#fff", fontSize: 11,
          }}>
            <PushpinOutlined style={{ color: atPinned ? "#FFB98C" : "#9AA7B0" }} />
            {atPinned ? (
              <>
                <span>Marked-up view</span>
                <span
                  onClick={() => setOverlayOn((v) => !v)}
                  style={{ cursor: "pointer", textDecoration: "underline", opacity: 0.85 }}
                >
                  {overlayOn ? "hide markup" : "show markup"}
                </span>
              </>
            ) : (
              <>
                <span style={{ opacity: 0.8 }}>Markup is off-view</span>
                <span onClick={goToPinnedView} style={{ cursor: "pointer", textDecoration: "underline" }}>
                  reset to it
                </span>
              </>
            )}
            <ViewpointNote note={[viewpoint.note, viewpoint.geometryNote].filter(Boolean).join(" ")} />
            {issue?.markupView?.savedByName && (
              <span style={{ opacity: 0.55 }}>· pinned by {issue.markupView.savedByName}</span>
            )}
          </div>
        )}

        {/* ── previewing another version ───────────────────────────── */}
        {previewing && (
          <div style={{
            position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)", zIndex: 7,
            background: "rgba(15,23,42,.92)", border: "1px solid rgba(255,255,255,.14)",
            borderRadius: 6, padding: "6px 10px", color: "#fff", fontSize: 11.5,
            display: "flex", alignItems: "center", gap: 10, maxWidth: "70%",
          }}>
            <span style={{ opacity: 0.75 }}>Viewing</span>
            <b style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{preview.label}</b>
            <span style={{ opacity: 0.55 }}>· not this issue's source</span>
            {canEdit && (
              <Button size="small" type="primary" ghost icon={<PaperClipOutlined />}
                      loading={!!attaching}
                      onClick={() => attachVersion(preview)}>
                Attach to issue
              </Button>
            )}
            <Button size="small" type="text" style={{ color: "#fff" }} icon={<CloseOutlined />}
                    onClick={() => previewVersion(null)} />
          </div>
        )}

        {/* ── versions panel ───────────────────────────────────────── */}
        {sourcesOpen && (
          <div style={{
            position: "absolute", top: 44, left: 8, zIndex: 8, width: 330,
            maxHeight: "calc(100% - 60px)", overflowY: "auto",
            background: "rgba(15,18,21,.97)", border: "1px solid #2B3238", borderRadius: 6,
            color: "#D7DDE2", boxShadow: "0 8px 24px rgba(0,0,0,.45)",
          }}>
            <div style={{
              padding: "8px 10px", borderBottom: "1px solid #23282C", display: "flex",
              alignItems: "center", gap: 8,
            }}>
              <b style={{ fontSize: 11.5, letterSpacing: ".05em" }}>VERSIONS OF THIS MODEL</b>
              <span style={{ flex: 1 }} />
              <CloseOutlined style={{ cursor: "pointer", fontSize: 11 }} onClick={() => setSourcesOpen(false)} />
            </div>
            {!sources ? (
              <div style={{ padding: 20, textAlign: "center" }}><Spin size="small" /></div>
            ) : (
              <div style={{ padding: "4px 0 8px" }}>
                {versionRows.length === 0 && (
                  <div style={{ padding: "12px 10px", fontSize: 11.5, color: "#7C8B96" }}>
                    This project has no streamable model versions yet.
                  </div>
                )}
                {versionRows.map((row, i) => {
                  const on = row.manifestPath === activeManifest;
                  const head = row.group && (i === 0 || versionRows[i - 1].group !== row.group);
                  return (
                    <div key={row.key}>
                      {head && (
                        <div style={{
                          padding: "8px 10px 3px", fontSize: 9.5, letterSpacing: ".08em",
                          color: "#7C8B96", fontWeight: 700,
                        }}>{row.group}</div>
                      )}
                      <div
                        onClick={() => row.streamable && previewVersion(row)}
                        title={row.streamable ? "Load this version into the canvas" : row.why}
                        style={{
                          padding: "6px 10px", cursor: row.streamable ? "pointer" : "not-allowed",
                          opacity: row.streamable ? 1 : 0.45,
                          background: on ? "rgba(254,105,25,.16)" : "transparent",
                          borderLeft: on ? "3px solid #FE6919" : "3px solid transparent",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{
                            flex: 1, minWidth: 0, fontSize: 12, fontWeight: on ? 700 : 500,
                            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                          }}>{row.label}</span>
                          {row.live && <span style={{ fontSize: 9, color: "#4FC92A", fontWeight: 700 }}>LIVE</span>}
                          {row.isSource && <span style={{ fontSize: 9, color: "#FFB98C", fontWeight: 700 }}>SOURCE</span>}
                        </div>
                        <div style={{ fontSize: 10, color: "#7C8B96", marginTop: 2, display: "flex", gap: 8, alignItems: "center" }}>
                          {row.when ? <span>{row.when}</span> : null}
                          {row.by ? <span>· {row.by}</span> : null}
                          {row.change ? <NodeDelta change={row.change} /> : null}
                        </div>
                        {row.narrative ? (
                          <div style={{
                            fontSize: 10.5, color: "#93A2AC", marginTop: 3, lineHeight: 1.35,
                            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
                          }}>{row.narrative}</div>
                        ) : null}
                        {!row.streamable && (
                          <div style={{ fontSize: 10, color: "#7C8B96", marginTop: 3 }}>{row.why}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {!isGLLoaded && !errText && (
          <div style={{
            position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
            color: "rgba(255,255,255,.7)", fontSize: 12, background: "rgba(0,0,0,.25)",
          }}>
            Loading model… {pct ? `${pct}%` : ""}
          </div>
        )}
        {errText && (
          <div style={{
            position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
            color: "#FCA5A5", fontSize: 12, padding: 16, textAlign: "center",
          }}>{errText}</div>
        )}

        {/* The pin overlay must not take the mouse away from the model.
            This layer used to be pointerEvents:auto across the whole
            pane, which swallowed every hover and click before the
            viewer's own canvas listeners saw them - so nothing in the 3D
            view could be hovered, picked or orbited. The pins' floating
            controls opt back in themselves. */}
        {isGLLoaded && (
          <div style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 4 }}>
            <div style={{ pointerEvents: "none", width: "100%", height: "100%" }}>
              <Issue3DPins
                issue={issue}
                canEdit={canEdit}
                pinModeRef={pinModeRef}
                pendingUniqueId={pendingPinUniqueId}
                onPendingHandled={() => setPendingPinUniqueId(null)}
                onIssueUpdated={onIssueUpdated}
                currentUser={userInfo}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
